import { useEffect, useState } from "react";
import { jobsApi, ApiError } from "@/api";
import type { ClientSearchResult, ClientCreateAttachInput } from "@/types";
import { Button, TextField, TextAreaField } from "@/components/ui";
import { FormDrawer } from "./FormDrawer";

type Mode = "search" | "create";

/** Add a client to the job — search-first to avoid duplicate stubs (Brief 02
 *  §3.3). Pick a match → attach; or fall through to create-and-attach. The
 *  "Set as lead" choice applies to whichever path is taken. */
export function AddClientDrawer({
  jobId,
  onClose,
  onSaved,
}: {
  jobId: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [mode, setMode] = useState<Mode>("search");
  const [makeLead, setMakeLead] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Search.
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<ClientSearchResult[]>([]);
  const [searching, setSearching] = useState(false);

  // Create form.
  const [isCompany, setIsCompany] = useState(false);
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [line1, setLine1] = useState("");
  const [line2, setLine2] = useState("");
  const [line3, setLine3] = useState("");
  const [city, setCity] = useState("");
  const [county, setCounty] = useState("");
  const [postcode, setPostcode] = useState("");
  const [notes, setNotes] = useState("");

  // Debounced search; needs ≥2 chars (the endpoint returns empty under 2 anyway).
  useEffect(() => {
    if (mode !== "search") return;
    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(() => {
      jobsApi
        .searchClients(term, 10)
        .then((rows) => {
          if (!cancelled) setResults(rows);
        })
        .catch(() => {
          if (!cancelled) setResults([]);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query, mode]);

  async function attach(client: ClientSearchResult) {
    if (busy) return;
    setBusy(true);
    setError(undefined);
    try {
      await jobsApi.attachClient({ job_id: jobId, client_id: client.id, make_lead: makeLead });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not attach the client.");
      setBusy(false);
    }
  }

  async function create() {
    if (busy) return;
    if (isCompany ? !company.trim() : !(first.trim() && last.trim())) {
      setError(isCompany ? "Enter the company name." : "Enter the client's first and last name.");
      return;
    }
    setBusy(true);
    setError(undefined);
    const input: ClientCreateAttachInput = {
      job_id: jobId,
      make_lead: makeLead,
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
      address_line_1: line1.trim() || undefined,
      address_line_2: line2.trim() || undefined,
      address_line_3: line3.trim() || undefined,
      city: city.trim() || undefined,
      county: county.trim() || undefined,
      postcode: postcode.trim() || undefined,
      notes: notes.trim() || undefined,
    };
    if (isCompany) {
      input.is_company = true;
      input.company_name = company.trim();
    }
    // Send the contact name whenever provided — required for an individual,
    // optional (but kept) for a company.
    if (first.trim()) input.contact_first_name = first.trim();
    if (last.trim()) input.contact_last_name = last.trim();
    try {
      await jobsApi.createAndAttachClient(input);
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not create the client.");
      setBusy(false);
    }
  }

  return (
    <FormDrawer
      title="Add client"
      busy={busy}
      onClose={onClose}
      footer={
        mode === "search" ? (
          <>
            <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button variant="secondary" onClick={() => { setMode("create"); setError(undefined); }} disabled={busy}>
              Create new client
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={() => { setMode("search"); setError(undefined); }} disabled={busy}>
              ← Back to search
            </Button>
            <Button variant="primary" onClick={create} loading={busy}>Create &amp; attach</Button>
          </>
        )
      }
    >
      {error ? <p className="leads__error" role="alert" onClick={() => setError(undefined)}>{error}</p> : null}

      <label className="lead-drawer__check">
        <input type="checkbox" checked={makeLead} onChange={(e) => setMakeLead(e.target.checked)} />
        Set as lead client
      </label>

      {mode === "search" ? (
        <section className="job-drawer__section">
          <input
            className="leads__search"
            type="search"
            placeholder="Search existing clients (name, company, email, postcode)…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          {query.trim().length > 0 && query.trim().length < 2 ? (
            <p className="job-drawer__muted">Keep typing — at least 2 characters.</p>
          ) : null}
          {searching ? <p className="job-drawer__muted">Searching…</p> : null}
          {!searching && query.trim().length >= 2 && results.length === 0 ? (
            <p className="job-drawer__muted">No matches — use “Create new client” below.</p>
          ) : null}
          {results.length > 0 ? (
            <ul className="job-client-results">
              {results.map((c) => (
                <li key={c.id}>
                  <div className="job-client-match">
                    <div className="job-client-match__info">
                      <span className="job-client-result__name">
                        {c.display} {c.is_company ? <span className="job-client-tag">Company</span> : null}
                      </span>
                      <span className="job-client-result__email">
                        {[c.email, c.postcode].filter(Boolean).join(" · ") || "No contact details"}
                      </span>
                    </div>
                    <Button variant="secondary" size="sm" disabled={busy} onClick={() => attach(c)}>Attach</Button>
                  </div>
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : (
        <>
          <section className="job-drawer__section">
            <h3>Contact</h3>
            <label className="lead-drawer__check">
              <input type="checkbox" checked={isCompany} onChange={(e) => setIsCompany(e.target.checked)} />
              This client is a company
            </label>
            {isCompany ? (
              <TextField label="Company name" required value={company} onChange={(e) => setCompany(e.target.value)} />
            ) : null}
            {/* Contact name is kept for companies too — the named person to deal
                with. Required for an individual; optional alongside a company. */}
            <div className="lead-drawer__two">
              <TextField label="First name" required={!isCompany} value={first} onChange={(e) => setFirst(e.target.value)} />
              <TextField label="Last name" required={!isCompany} value={last} onChange={(e) => setLast(e.target.value)} />
            </div>
            <div className="lead-drawer__two">
              <TextField label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              <TextField label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
          </section>

          <section className="job-drawer__section">
            <h3>Address</h3>
            <TextField label="Address line 1" value={line1} onChange={(e) => setLine1(e.target.value)} />
            <TextField label="Address line 2" value={line2} onChange={(e) => setLine2(e.target.value)} />
            <TextField label="Address line 3" value={line3} onChange={(e) => setLine3(e.target.value)} />
            <div className="lead-drawer__two">
              <TextField label="City / town" value={city} onChange={(e) => setCity(e.target.value)} />
              <TextField label="County" value={county} onChange={(e) => setCounty(e.target.value)} />
            </div>
            <TextField label="Postcode" value={postcode} onChange={(e) => setPostcode(e.target.value.toUpperCase())} />
          </section>

          <section className="job-drawer__section">
            <h3>Notes</h3>
            <TextAreaField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </section>
        </>
      )}
    </FormDrawer>
  );
}
